import { DraftedHandoffCard } from '@fruitful/agent-ui';
import { type SendToGuideWidgetProps } from '@fruitful/types';
import Clipboard from '@react-native-clipboard/clipboard';
import React, { useCallback, useState } from 'react';
import { useToastController } from 'smoothie';

import { StorageKeys } from '../../../common/constants';
import { useStorage } from '../../../common/providers/StorageProvider';
import { useSendGuideMessage } from '../../../guideMessaging/hooks/useSendGuideMessage';
import { captureException } from '../../../logger/Logger';

const readSentIds = (raw: string | null | undefined): Record<string, true> => {
    if (raw == null || raw === '') {
        return {};
    }

    try {
        const parsed: unknown = JSON.parse(raw);
        if (
            parsed == null ||
            typeof parsed !== 'object' ||
            Array.isArray(parsed)
        ) {
            return {};
        }

        return parsed as Record<string, true>;
    } catch {
        return {};
    }
};

interface SendToGuideWidgetComponentProps extends SendToGuideWidgetProps {
    messageId: string;
}

export const SendToGuideWidget = ({
    messageBody,
    messageId,
}: SendToGuideWidgetComponentProps) => {
    const storage = useStorage();
    const toast = useToastController();
    const { sendGuideMessage, canSend } = useSendGuideMessage();

    const [hasSent, setHasSent] = useState(() => {
        const sentIds = readSentIds(
            storage.getString(StorageKeys.SPROUT_SEND_TO_GUIDE_SENT_IDS),
        );
        return sentIds[messageId] === true;
    });
    const [isSending, setIsSending] = useState(false);

    const isDisabled = hasSent || isSending;

    const persistSent = useCallback(() => {
        const sentIds = readSentIds(
            storage.getString(StorageKeys.SPROUT_SEND_TO_GUIDE_SENT_IDS),
        );
        storage.setString(
            StorageKeys.SPROUT_SEND_TO_GUIDE_SENT_IDS,
            JSON.stringify({
                ...sentIds,
                [messageId]: true,
            }),
        );
    }, [messageId, storage]);

    const handlePress = useCallback(() => {
        if (hasSent || isSending) {
            return;
        }

        if (!canSend) {
            toast.show('Unable to message your Guide right now');
            return;
        }

        setIsSending(true);

        void (async () => {
            try {
                await sendGuideMessage(messageBody);
                persistSent();
                setHasSent(true);
            } catch (error) {
                captureException(error);
                toast.show('Failed to send message to your Guide');
            } finally {
                setIsSending(false);
            }
        })();
    }, [
        canSend,
        hasSent,
        isSending,
        messageBody,
        persistSent,
        sendGuideMessage,
        toast,
    ]);

    const handleCopy = useCallback(() => {
        Clipboard.setString(messageBody);
        toast.show('Copied!', { duration: 1000 });
    }, [messageBody, toast]);

    return (
        <DraftedHandoffCard
            label="Message to your Guide"
            body={messageBody}
            actionLabel={hasSent ? 'Sent' : 'Send in Fruitful chat'}
            actionDisabled={isDisabled}
            onAction={handlePress}
            onCopy={handleCopy}
        />
    );
};
